<template>
  <v-row dense>
    <v-col cols="12">
      <v-toolbar dense :flat="true">
        <v-row dense>
          <v-col cols="4">
            <v-text-field
              hide-details
              prepend-icon="mdi-text-search"
              single-line
              v-model="searchKeywords"
              @keyup.enter.native="search()"
            ></v-text-field>
          </v-col>
          <v-col cols="2">
            <v-btn small color="primary" @click="search()">Search</v-btn>
          </v-col>
          <v-col cols="2">
            <v-select
              label="Expire Within Days"
              v-model="expiryWithin" :items="[30, 60, 90, 180, 'all']" @change="reloadTable">
            </v-select>
          </v-col>
        </v-row>
      </v-toolbar>
    </v-col>
    <v-col cols="12">
      <v-data-table
        :dense="true"
        :headers="headers"
        :fixed-header="true"
        :items="services"
        :disable-pagination="true"
        :hide-default-footer="true"
        show-select
        v-model="selectedServices"
        height="calc(100vh - 68px - 130px - 24px)"
      >
        <template v-slot:item.product_prices="{ item }">
          <div v-if="item.reactivation_fee" class="warning--text">
            {{ item.reactivation_fee.description }}:
            {{ item.reactivation_fee.currency }} {{ item.reactivation_fee.amount }} before tax.
            Renewal waits for SGNIC clearance.
          </div>
          <v-select
            v-model="item.default_term"
            :items="item.product_prices"
            @change="setSelectedItem()"
          ></v-select>
          <v-row dense v-if="item.addons.length > 0">
            <h4>Addons:</h4>
          </v-row>
          <v-row dense v-for="addon in item.addons" :key="addon.id">
            <v-col cols="12">
              <v-select
                :label="addon.service_identity"
                v-model="item.default_term"
                :items="addon.product_prices"
                :readonly="true"
              ></v-select>
            </v-col>
          </v-row>
        </template>
      </v-data-table>
    </v-col>
    <v-col cols="12" class="v-bottom-navigation--fixed text-right">
      <h3>Renewal Total: ${{ total }}</h3>
      <v-btn color="primary" :loading="processing" :disabled="!canRenew"
             @click="renew()">Add Renewal to Cart</v-btn>
    </v-col>
  </v-row>
</template>

<script>
import { create, all } from 'mathjs';

export default {
  name: 'ServiceBulkRenew',
  data() {
    return {
      expiryWithin: 60,
      headers: [
        {
          text: 'Product',
          align: 'start',
          sortable: false,
          value: 'service_identity',
        },
        {
          text: 'Expiry',
          align: 'start',
          sortable: false,
          value: 'expiry_date',
        },
        {
          text: 'Renew Term',
          align: 'start',
          sortable: false,
          value: 'product_prices',
        },
      ],
      services: [],
      initialServices: [],
      selectedServices: [],
      processing: false,
      canRenew: false,
      total: 0.00,
      searchKeywords: '',
    };
  },
  created() {
    this.reloadTable();
  },
  methods: {
    reloadTable() {
      this.$api.get(`partner/service/pending-renewal?expiry_within=${this.expiryWithin}`).then((response) => {
        this.$set(this, 'services', response.data);
        this.services.forEach((service, index) => {
          service.product_prices.forEach((price, priceIndex) => {
            if (priceIndex === 0) {
              this.services[index].default_term = price.value;
            }
            if (parseInt(price.term_month, 0) === service.service_month_term) {
              this.services[index].default_term = price.value;
            }
            this.services[index].product_prices[priceIndex].text
              += ` ($${this.services[index].product_prices[priceIndex].product_price_monthly}/month)`;
          });
        });
        this.$set(this, 'initialServices', response.data);
      });
    },
    renew() {
      const fees = this.selectedServices.filter((service) => service.reactivation_fee)
        .map((service) => `${service.reactivation_fee.description}: `
          + `${service.reactivation_fee.currency} ${service.reactivation_fee.amount} before tax`);
      if (fees.length && !window.confirm(`${fees.join('\n')}\nContinue with these additional fees?`)) {
        return;
      }
      this.processing = true;
      const postSelectedServices = [];
      this.selectedServices.forEach((selectedService) => {
        postSelectedServices.push(selectedService);
        if (selectedService.addons.length > 0) {
          selectedService.addons.forEach((addon) => {
            const localAddon = addon;
            localAddon.default_term = selectedService.default_term;
            postSelectedServices.push(localAddon);
          });
        }
      });
      this.$api.post('partner/service/bulk-renew', {
        services: postSelectedServices,
      }).then((data) => {
        const successMessage = 'Your renewal request is successfully added to the shopping cart';
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'success',
          message: successMessage,
        });
        this.$store.dispatch('syncShoppingCart', data.data);
        setTimeout(() => {
          this.goToCart();
        }, 500);
      }).finally(() => {
      });
    },
    goToCart() {
      const { brand } = this.$store.state;
      if (this.$store.state.user.role) {
        this.$router.push({
          name: 'Retail.ShoppingCart',
          params: { brand },
        })
          .catch(() => {
          });
      } else {
        this.$router.push({
          name: 'Retail.GuestShoppingCart',
          params: { brand },
        })
          .catch(() => {
          });
      }
    },
    setSelectedItem() {
      const config = {};
      const math = create(all, config);
      this.total = 0;

      this.selectedServices.forEach((service) => {
        if (service.reactivation_fee) {
          this.total = math.add(this.total, Number(service.reactivation_fee.total));
        }
        service.product_prices.forEach((price) => {
          if (price.value === service.default_term) {
            this.total = math.format(math.add(this.total, price.product_price), {
              precision: 14,
            });
          }
        });

        service.addons.forEach((addon) => {
          addon.product_prices.forEach((price) => {
            if (price.value === service.default_term) {
              this.total = math.format(math.add(this.total, price.product_price), {
                precision: 14,
              });
            }
          });
        });
      });
    },
    search() {
      if (this.searchKeywords !== '') {
        this.$set(this, 'services',
          this.initialServices.filter((service) => service.service_identity
            .toLowerCase()
            .includes(this.searchKeywords.toLowerCase()) === true));
      } else {
        this.$set(this, 'services', this.initialServices);
      }
    },
  },
  watch: {
    selectedServices: {
      handler(newSelectedServices) {
        if (newSelectedServices.length > 0) {
          this.canRenew = true;
        } else {
          this.canRenew = false;
        }
        this.setSelectedItem();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.scroller {
  height: calc(100vh - 68px - 48px - 10px)
}
</style>
